import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import ProductMap from '../../../components/ui/ProductMap';
import { apiRequest } from '../../../services/apiClient';
import { useAuth } from '../../../context/AuthContext';

const FeaturedProducts = () => {
  const { user } = useAuth();
  const [savedProducts, setSavedProducts] = useState(new Set());
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchFeaturedProducts = async () => {
      setIsLoading(true);
      try {
        const response = await apiRequest('/products?pageSize=12');
        const items = Array.isArray(response) ? response : response?.items || [];

        if (!cancelled) {
          setFeaturedProducts(items);
        }
      } catch (error) {
        console.error('Error fetching featured products:', error);
        if (!cancelled) {
          setFeaturedProducts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchFeaturedProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatPrice = (price) => {
    const numeric = Number(price);
    if (!Number.isFinite(numeric)) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(numeric);
  };

  const getProductImage = (product) => {
    const firstImage = Array.isArray(product?.images) ? product.images.find(Boolean) : null;
    return firstImage || product?.imageUrl || '/assets/images/no_image.png';
  };

  const handleSaveProduct = (productId) => {
    setSavedProducts((prev) => {
      const newSaved = new Set(prev);
      if (newSaved?.has(productId)) {
        newSaved?.delete(productId);
      } else {
        newSaved?.add(productId);
      }
      return newSaved;
    });
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4 font-heading">
            Featured Agricultural Products
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Discover fresh, high-quality agricultural products from certified farms across the country
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-[60%]">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)]?.map((_, index) => (
                  <div key={index} className="bg-surface rounded-lg overflow-hidden shadow-elevation-1 p-4 animate-pulse">
                    <div className="h-40 bg-secondary-100 rounded-md mb-4"></div>
                    <div className="h-4 bg-secondary-100 rounded w-2/3 mb-2"></div>
                    <div className="h-4 bg-secondary-100 rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-secondary-100 rounded w-1/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredProducts?.map((product) => (
                  <Link
                    key={product?.id}
                    to={`/property-details?id=${product?.id}`}
                    className="bg-surface rounded-lg overflow-hidden shadow-elevation-1 hover:shadow-elevation-3
                           transition-all duration-300 ease-out micro-interaction group cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                        <Image
                        src={getProductImage(product)}
                        alt={product?.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />

                      {(product?.organic || product?.isOrganic) && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-success text-white px-2 py-1 rounded-md text-xs font-medium">
                            Organic
                          </span>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e?.preventDefault();
                          handleSaveProduct(product?.id);
                        }}
                        className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200
                                   ${savedProducts?.has(product?.id)
                                     ? 'bg-error text-white' :'bg-white/90 text-text-secondary hover:bg-white hover:text-error'
                                   }`}
                        aria-label={savedProducts?.has(product?.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Icon
                          name="Heart"
                          size={16}
                          fill={savedProducts?.has(product?.id) ? 'currentColor' : 'none'}
                        />
                      </button>

                      {product?.daysListed <= 3 && (
                        <div className="absolute bottom-3 left-3">
                          <span className="bg-primary text-white px-2 py-1 rounded-md text-xs font-medium">
                            New Listing
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <div className="mb-3">
                        <h3 className="text-lg font-semibold text-text-primary mb-1 group-hover:text-primary transition-colors">
                          {product?.title}
                        </h3>
                        <p className="text-text-secondary text-sm flex items-center">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          {product?.location || 'Konum belirtilmemiş'}
                        </p>
                      </div>

                      <div className="mb-4">
                        <p className="text-2xl font-bold text-primary">
                          {formatPrice(product?.price)} {product?.unit ? ` / ${product.unit}` : ''}
                        </p>
                      </div>

                      <div className="flex items-center justify-between text-sm text-text-secondary mb-4">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Icon name="Package" size={14} className="mr-1" />
                            {product?.quantity} {product?.unit}
                          </span>
                          <span className="flex items-center">
                            <Icon name="Leaf" size={14} className="mr-1" />
                            {product?.category || 'Kategori belirtilmemiş'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center space-x-2">
                          <Icon name="Tractor" size={16} className="text-text-secondary" />
                            <div>
                              <span className="text-sm font-medium text-text-primary">
                                {user?.id && product?.seller?.id && user.id === product.seller.id
                                  ? 'Size Ait'
                                  : product?.seller?.name || 'Satıcı bilgisi yok'}
                              </span>
                            <p className="text-xs text-text-secondary">Seller</p>
                          </div>
                        </div>

                        <span className="text-sm font-medium text-primary hover:text-primary-700 transition-colors">
                          View Details
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}

                {!featuredProducts?.length && (
                  <div className="col-span-full text-center py-10 border border-border rounded-lg bg-surface">
                    <Icon name="Package" size={40} className="mx-auto text-text-secondary mb-3" />
                    <p className="text-text-secondary">No products found yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:w-[40%]">
            <div className="sticky top-20">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Product Locations</h3>
              <ProductMap products={featuredProducts} />
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <Link
            to="/property-listings"
            className="inline-flex items-center px-8 py-3 bg-primary text-white rounded-md font-semibold
                     hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                     transition-all duration-200 ease-out micro-interaction"
          >
            View All Products
            <Icon name="ArrowRight" size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
